import { useEffect, useRef, useState } from 'react';
import { ImageIcon } from '@phosphor-icons/react';
import { Field } from '@/modules/primitives3D/editorShell';
import { FieldLabel, FieldHint, SectionTitle } from '@/components/ui/text';
import { AutoGrowTextarea } from '../AutoGrowTextarea';
import { CoverImageTile } from '../CoverImageTile';
import { PillButton } from '../PillButton';
import type { PublishFlowImagesApi } from '../usePublishFlowImages';

const SCENE_INTRO_MAX = 500;

interface Props {
  sceneIntroduction: string;
  coverImages: PublishFlowImagesApi;
  projectInstanceId?: string | null;
  sceneOriginId?: string | null;
  onChange: (patch: { sceneIntroduction?: string }) => void;
}

export function SceneInfoStep({
  sceneIntroduction,
  coverImages,
  projectInstanceId,
  sceneOriginId,
  onChange,
}: Props) {
  const { sceneLandscape, projectLandscape } = coverImages;
  const [copying, setCopying] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);
  const coverWrapRef = useRef<HTMLDivElement>(null);
  const introWrapRef = useRef<HTMLDivElement>(null);
  const [introMinH, setIntroMinH] = useState(0);

  useEffect(() => {
    const compute = () => {
      const button = coverWrapRef.current?.querySelector('button');
      const c = button?.getBoundingClientRect();
      const i = introWrapRef.current?.getBoundingClientRect();
      if (!c || !i) return;
      const target = c.bottom - i.top;
      setIntroMinH(Math.max(60, target));
    };
    compute();
    const observer = new ResizeObserver(compute);
    if (coverWrapRef.current) observer.observe(coverWrapRef.current);
    if (introWrapRef.current) observer.observe(introWrapRef.current);
    window.addEventListener('resize', compute);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', compute);
    };
  }, []);

  const canReuseProjectCover = !sceneLandscape.url && Boolean(projectLandscape.url);

  const applyProjectCover = async () => {
    if (!projectLandscape.originalUrl) return;
    setCopyError(null);
    setCopying(true);
    try {
      await coverImages.saveImage('SceneLandscape', {
        imageOriginalUrl: projectLandscape.originalUrl,
        imageCroppedUrl: projectLandscape.url ?? undefined,
        crop: projectLandscape.crop ?? undefined,
      });
    } catch {
      setCopyError('Could not reuse the project cover. Please try again.');
    } finally {
      setCopying(false);
    }
  };

  return (
    <div className="grid grid-cols-2 divide-x-lureo-border-width divide-lureo-line h-full">
      <div className="space-y-4 min-w-0 pr-7">
        <SectionTitle>Details</SectionTitle>

        <Field>
          <FieldLabel mb>Introduction</FieldLabel>
          <div ref={introWrapRef}>
            <AutoGrowTextarea
              value={sceneIntroduction}
              onChange={(e) => onChange({ sceneIntroduction: e.target.value })}
              placeholder="What happens in this story?"
              maxLength={SCENE_INTRO_MAX}
              minHeightPx={introMinH}
            />
          </div>
        </Field>
      </div>

      <div className="space-y-3 min-w-0 pl-7">
        <SectionTitle>Story image</SectionTitle>

        <div ref={coverWrapRef}>
          <CoverImageTile
            aspect="16/9"
            fullWidth
            label="Landscape (16:9)"
            category="SceneLandscape"
            entry={sceneLandscape}
            displayUrl={sceneLandscape.url}
            projectInstanceId={projectInstanceId}
            sceneOriginId={sceneOriginId}
            onSave={(payload) => coverImages.saveImage('SceneLandscape', payload)}
            onRemove={() => coverImages.removeImage('SceneLandscape')}
          />
        </div>

        {canReuseProjectCover ? (
          <div className="flex items-center justify-between gap-2">
            <FieldHint>Click to upload, or reuse the cover from your project.</FieldHint>
            <PillButton
              icon={ImageIcon}
              label={copying ? 'Applying…' : 'Use project cover image'}
              loading={copying}
              onClick={() => void applyProjectCover()}
            />
          </div>
        ) : (
          <FieldHint>
            {sceneLandscape.url
              ? 'Click to replace.'
              : 'Click to upload a cover image for this story.'}
          </FieldHint>
        )}
        {copyError && <FieldHint>{copyError}</FieldHint>}
      </div>
    </div>
  );
}
